import { logError } from '@iwLogger/IwDataDogLogger';
import { useQueryClient } from '@tanstack/react-query';

import { extendConnection, revokeConnection } from './extendConnection';
import { ReconsentConnection } from './getReconsentConnections';
import { trackExtendOutcome, trackRevokeOutcome } from './reconsentEvents';
import { useStateKey } from '../../../../iwQueryClient/useStateKey';

type ReconsentFlow = {
    goToReconsentLoading: () => void;
    goToRevokeSuccess: (connection: ReconsentConnection) => void;
    goToReauth: (link: string, connection: ReconsentConnection) => void;
    goToError: () => void;
    markExtended: (onlineAccountId: string) => void;
};

export const useReconsentActions = ({
    flow,
    onExtended
}: {
    flow: ReconsentFlow;
    onExtended: (connection: ReconsentConnection) => void;
}) => {
    const stateKey = useStateKey();
    const queryClient = useQueryClient();

    const failExtend = (connection: ReconsentConnection, error: unknown) => {
        logError('Open banking reconsent: failed to extend connection', {
            onlineAccountId: connection.onlineAccountId,
            error: error instanceof Error ? error.message : 'Unknown error'
        });
        trackExtendOutcome({
            ob_extend_outcome: 'failed',
            bank_name: connection.bankName
        });
        flow.goToError();
    };

    const handleExtend = async (connection: ReconsentConnection) => {
        flow.goToReconsentLoading();

        // Only the call we are measuring belongs in the try: what follows a
        // renewal cannot make the renewal itself a failure.
        let result;
        try {
            result = await extendConnection({
                stateKey,
                onlineAccountId: connection.onlineAccountId
            });
        } catch (error) {
            failExtend(connection, error);
            return;
        }

        if (result.action_needed === 'no_action_needed') {
            trackExtendOutcome({
                ob_extend_outcome: 'extended',
                bank_name: connection.bankName
            });
            flow.markExtended(connection.onlineAccountId);
            await queryClient.invalidateQueries({
                queryKey: ['online_accounts', stateKey]
            });
            onExtended(connection);
            return;
        }

        if (!result.user_input_link) {
            failExtend(
                connection,
                new Error(
                    `No user_input_link returned for ${result.action_needed}`
                )
            );
            return;
        }

        trackExtendOutcome({
            ob_extend_outcome: 'reauth_required',
            bank_name: connection.bankName
        });
        flow.goToReauth(result.user_input_link, connection);
    };

    const handleConfirmRevoke = async (connection: ReconsentConnection) => {
        flow.goToReconsentLoading();

        try {
            await revokeConnection({
                stateKey,
                onlineAccountId: connection.onlineAccountId
            });
        } catch (error) {
            logError('Open banking reconsent: failed to revoke connection', {
                onlineAccountId: connection.onlineAccountId,
                error: error instanceof Error ? error.message : 'Unknown error'
            });
            trackRevokeOutcome({
                ob_revoke_outcome: 'failed',
                bank_name: connection.bankName
            });
            flow.goToError();
            return;
        }

        trackRevokeOutcome({
            ob_revoke_outcome: 'revoked',
            bank_name: connection.bankName
        });
        await queryClient.invalidateQueries({
            queryKey: ['online_accounts', stateKey]
        });
        flow.goToRevokeSuccess(connection);
    };

    return { handleExtend, handleConfirmRevoke };
};
